/**
 * 참가자에게 돌려주는 확정 결과 envelope (TASK-2026-004 REV-007, TASK-2026-003 CR-01 §3.2).
 *
 * 서버가 `GET /api/v1/session/result`로 내려주는 `ccatlas.session-result/1` 한 개
 * envelope이 유일한 원본이다. 이 모듈은 **다시 만들지 않는다** —— 받은 문자열을
 * 그대로 들고, 그 문자열이 스스로의 digest와 맞는지만 확인한다.
 *
 * 왜 재조립하지 않는가: envelope의 digest는 canonical 문자열 하나를 가리킨다.
 * 화면이 파싱했다가 다시 직렬화하면 키 순서·수 표기 하나만 달라져도 digest가
 * 가리키는 바이트와 어긋나고, 참가자가 내려받은 파일은 서버가 확정한 것과 다른
 * 문서가 된다. 그러면 그 파일로는 아무것도 증명하지 못한다.
 */

import { canonicalize, sha256Hex } from '../telemetry/canonical';

export const RESULT_SCHEMA = 'ccatlas.session-result/1';
export const CANONICALIZATION = 'ccatlas-cjson/1';

export interface ResultBodyView {
  readonly complete: boolean;
  readonly session_ref: string;
  readonly campaign: { readonly id: string };
  readonly blocking_issues: readonly { readonly code: string }[];
}

export interface VerifiedResult {
  /** 서버가 보낸 바이트 그대로. 표시와 내려받기가 이 문자열을 쓴다. */
  readonly text: string;
  readonly digest: string;
  readonly body: ResultBodyView;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * envelope 문자열이 스스로의 digest와 맞는가.
 *
 * 서버 `result.ts`의 `verifyStoredResult`와 같은 검사다. 맞지 않으면 `undefined`이고,
 * 화면은 그것을 확정 결과로 보여 주지 않는다.
 */
export function verifyResultText(text: string): VerifiedResult | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (!isRecord(parsed) || parsed['schema'] !== RESULT_SCHEMA || parsed['canonicalization'] !== CANONICALIZATION) {
    return undefined;
  }
  const digest = parsed['digest'];
  const body = parsed['body'];
  if (typeof digest !== 'string' || !isRecord(body)) {
    return undefined;
  }
  // 받은 문자열이 canonical 형태 그대로인지까지 본다. 아니면 서버의 그 바이트가 아니다.
  if (canonicalize(parsed) !== text || digest !== `sha256:${sha256Hex(canonicalize(body))}`) {
    return undefined;
  }
  const campaign = body['campaign'];
  const issues = body['blocking_issues'];
  return {
    text,
    digest,
    body: {
      complete: body['complete'] === true,
      session_ref: typeof body['session_ref'] === 'string' ? body['session_ref'] : '',
      campaign: { id: isRecord(campaign) && typeof campaign['id'] === 'string' ? campaign['id'] : '' },
      blocking_issues: Array.isArray(issues)
        ? issues
            .filter((issue): issue is { code: string } => isRecord(issue) && typeof issue['code'] === 'string')
            .map((issue) => ({ code: issue.code }))
        : [],
    },
  };
}

/** 확정 결과 파일 이름. 전역 표준의 계층 식별자 + 날짜 규칙을 따른다. */
export function resultFileName(sessionRef: string, at: number = Date.now()): string {
  const date = new Date(at);
  const pad = (value: number): string => String(value).padStart(2, '0');
  const stamp = `${String(date.getFullYear())}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  const safe = /^[A-Za-z0-9_-]{1,64}$/.test(sessionRef) ? sessionRef : 'session';
  return `ColorCubeAtlas_GateF1_Collect_Result_${safe}_${stamp}.json`;
}
